/**
 * Collection Build design-sheet UI constants + session mapping helpers.
 * Isolated from Image Generator design-sheet UI.
 */

import { CollectionBuildHistoryRecord } from "../../app/api/collectionBuild";
import {
  CollectionBuildSheetSlot,
  CollectionBuildSheetVersion,
} from "./collectionBuildConstants";

/** Display labels for CB design-sheet slides (Look / Apparel / Print|Surface|Embroidery). */
export const COLLECTION_BUILD_SHEET_SLOT_LABELS = {
  look: "Look",
  apparel: "Apparel",
  print: "Print",
  surface: "Surface",
  embroidery: "Embroidery",
  pattern: "Pattern",
  sheet: "Sheet",
} as const;

export const collectionBuildSheetSlotLabel = (
  slotId: string,
  options?: { surface?: boolean }
): string => {
  if (slotId.endsWith(".apparel")) {
    return COLLECTION_BUILD_SHEET_SLOT_LABELS.apparel;
  }
  if (slotId === "pattern.print" || slotId === "pattern.embroidery") {
    return options?.surface
      ? COLLECTION_BUILD_SHEET_SLOT_LABELS.surface
      : COLLECTION_BUILD_SHEET_SLOT_LABELS.pattern;
  }
  if (slotId.endsWith(".print")) {
    return options?.surface
      ? COLLECTION_BUILD_SHEET_SLOT_LABELS.surface
      : COLLECTION_BUILD_SHEET_SLOT_LABELS.print;
  }
  if (slotId.endsWith(".embroidery")) {
    return COLLECTION_BUILD_SHEET_SLOT_LABELS.embroidery;
  }
  return COLLECTION_BUILD_SHEET_SLOT_LABELS.look;
};

/** Map look outputs → one pack per generation (slots = slides, not regen versions). */
export const collectionSheetsFromOutputs = (
  outputs: CollectionBuildHistoryRecord[]
): Record<string, CollectionBuildSheetVersion[]> => {
  const sheets: Record<string, CollectionBuildSheetVersion[]> = {};
  const lookOutputs = outputs
    .filter(item => item.outputType === "look")
    .slice()
    .sort(
      (left, right) =>
        new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
    );

  lookOutputs.forEach(item => {
    const designSheet = item.details?.designSheet;
    const slots = (designSheet?.slots || []).filter(slot => Boolean(slot.url));
    if (!slots.length) {
      return;
    }
    const lookId = `look-${item.outputNumber || item.id}`;
    const surface = Boolean(
      (item.details as { surfaceId?: string } | null | undefined)?.surfaceId
    );
    const mappedSlots: CollectionBuildSheetSlot[] = slots.map(
      (slot, index) => ({
        id: `${item.id}-sheet-${slot.id || index}`,
        slotId: String(slot.id || `slot-${index}`),
        label: collectionBuildSheetSlotLabel(String(slot.id || ""), {
          surface,
        }),
        imageUrl: slot.url,
        svgUrl: slot.svgUrl || null,
      })
    );
    const pack: CollectionBuildSheetVersion = {
      id: `${item.id}-sheet-pack`,
      outputId: item.id,
      imageUrl: mappedSlots[0].imageUrl,
      approvedAt: item.review?.approvedAt || null,
      slots: mappedSlots,
    };
    const list = sheets[lookId] || [];
    if (!list.some(existing => existing.outputId === item.id)) {
      list.push(pack);
    }
    sheets[lookId] = list;
  });
  return sheets;
};
